import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Trash2, Pencil, ImagePlus, X, Images } from "lucide-react";

const CATEGORIES = ["Máquinas", "Peças", "Serviços", "Estrutura"];
const API_BASE = process.env.REACT_APP_BACKEND_URL;
const ACCEPT = ".png,.jpg,.jpeg,.webp,.gif,.mp4,.webm";
const INITIAL = { title: "", description: "", category: "Máquinas", order: 0, media_type: "image", video_url: "" };

const mediaUrl = (it) => `${API_BASE}/api/gallery/${it.id}/file`;

export default function GalleryAdmin() {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState(INITIAL);
  const [file, setFile] = useState(null);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/gallery");
      setItems(data);
    } catch {
      setItems([]);
    }
  };
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const reset = () => { setForm(INITIAL); setFile(null); setEditing(null); if (fileRef.current) fileRef.current.value = ""; };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let id = editing;
      if (editing) {
        await api.put(`/gallery/${editing}`, { ...form, order: Number(form.order) || 0 });
        toast.success("Item atualizado!");
      } else {
        const { data } = await api.post("/gallery", { ...form, order: Number(form.order) || 0 });
        id = data.id;
        toast.success("Item cadastrado!");
      }
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        try {
          await api.post(`/gallery/${id}/file`, fd);
          toast.success(`Arquivo "${file.name}" enviado!`);
        } catch (err) {
          toast.error(`${file.name}: ${formatApiError(err)}`);
        }
      }
      reset();
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (it) => {
    setEditing(it.id);
    setForm({ title: it.title, description: it.description || "", category: it.category,
              order: it.order ?? 0, media_type: it.media_type || "image", video_url: it.video_url || "" });
    setFile(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (it) => {
    if (!window.confirm(`Excluir "${it.title}" da galeria?`)) return;
    try {
      await api.delete(`/gallery/${it.id}`);
      toast.success("Item excluído.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const inputCls =
    "mt-1.5 w-full bg-slate-900/70 border border-slate-700 rounded-md px-3 py-2.5 text-sm outline-none focus:border-orange-500 transition-colors placeholder:text-slate-600";
  const labelCls = "text-xs uppercase tracking-wider text-slate-400";

  return (
    <div data-testid="gallery-admin-page">
      <h1 className="display text-3xl font-extrabold uppercase">Galeria do Site</h1>
      <p className="mt-1 text-sm text-slate-500">
        Cadastre fotos (e futuramente vídeos) da empresa. Eles aparecem automaticamente na galeria pública.
      </p>

      <form onSubmit={submit} className="mt-6 card-tactical rounded-md p-6" data-testid="gallery-admin-form">
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <label className={labelCls}>Título *</label>
            <input data-testid="ga-title-input" required value={form.title} onChange={set("title")} className={inputCls} placeholder="Ex.: Centro de usinagem Romi em operação" />
          </div>
          <div>
            <label className={labelCls}>Categoria *</label>
            <select data-testid="ga-category-select" value={form.category} onChange={set("category")} className={inputCls}>
              {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Ordem de exibição</label>
            <input data-testid="ga-order-input" type="number" value={form.order} onChange={set("order")} className={inputCls} placeholder="0" />
          </div>
          <div>
            <label className={labelCls}>Arquivo (imagem ou vídeo)</label>
            <input
              ref={fileRef}
              data-testid="ga-file-input"
              type="file"
              accept={ACCEPT}
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="mt-1.5 w-full text-sm text-slate-400 file:mr-3 file:rounded-md file:border-0 file:bg-orange-600 file:px-4 file:py-2 file:text-xs file:font-semibold file:uppercase file:tracking-wider file:text-white hover:file:bg-orange-500 file:cursor-pointer"
            />
            {editing && <p className="mt-1 text-[11px] text-slate-600">Selecione um arquivo apenas se quiser substituir o atual.</p>}
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls}>Descrição</label>
            <textarea data-testid="ga-description-input" rows={2} value={form.description} onChange={set("description")} className={`${inputCls} resize-none`} placeholder="Legenda exibida junto à foto (opcional)" />
          </div>
        </div>
        <div className="mt-5 flex items-center gap-3">
          <button
            data-testid="ga-submit-button"
            type="submit"
            disabled={loading}
            className="inline-flex items-center gap-2 pill-accent rounded-md px-6 py-2.5 text-sm font-semibold uppercase tracking-wider disabled:opacity-60 transition"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
            {editing ? "Salvar alterações" : "Cadastrar na galeria"}
          </button>
          {editing && (
            <button type="button" data-testid="ga-cancel-button" onClick={reset} className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-slate-200">
              <X className="w-4 h-4" /> Cancelar edição
            </button>
          )}
        </div>
      </form>

      <div className="mt-8">
        {!items ? (
          <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-600" /></div>
        ) : !items.length ? (
          <div className="card-tactical rounded-md py-12 text-center" data-testid="ga-empty">
            <Images className="w-10 h-10 text-slate-700 mx-auto" strokeWidth={1.25} />
            <p className="mt-3 text-sm text-slate-500">Nenhuma foto cadastrada ainda.</p>
          </div>
        ) : (
          <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="ga-items-list">
            {items.map((it) => (
              <li key={it.id} className="card-tactical rounded-md overflow-hidden" data-testid={`ga-item-${it.id}`}>
                <div className="h-40 bg-slate-900 grid place-content-center overflow-hidden">
                  {it.file && it.media_type !== "video" ? (
                    <img src={mediaUrl(it)} alt={it.title} className="w-full h-40 object-cover" loading="lazy" />
                  ) : it.file && it.media_type === "video" ? (
                    <video src={mediaUrl(it)} className="w-full h-40 object-cover" muted />
                  ) : (
                    <Images className="w-8 h-8 text-slate-700" strokeWidth={1.25} />
                  )}
                </div>
                <div className="p-4">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-semibold text-slate-200 truncate">{it.title}</span>
                    <span className="mono text-[10px] text-orange-400 shrink-0">#{it.order ?? 0}</span>
                  </div>
                  <div className="mt-1 text-[11px] uppercase tracking-wider text-slate-500">
                    {it.category}{it.media_type === "video" ? " · vídeo" : ""}{it.file ? "" : " · sem arquivo"}
                  </div>
                  {it.description && <p className="mt-1.5 text-xs text-slate-500 line-clamp-2">{it.description}</p>}
                  <div className="mt-3 flex gap-2">
                    <button data-testid={`ga-edit-${it.id}`} onClick={() => startEdit(it)} className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-orange-400 transition-colors">
                      <Pencil className="w-3.5 h-3.5" /> Editar
                    </button>
                    <button data-testid={`ga-delete-${it.id}`} onClick={() => remove(it)} className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-red-400 transition-colors">
                      <Trash2 className="w-3.5 h-3.5" /> Excluir
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
